// Competency admin (Phase 4b) — HR defines the framework and assesses employees.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Plus, Trash2, Target } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

export default function CompetencyAdmin({ showToast }) {
  const [catalog, setCatalog] = useState(null);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(null);
  const [empId, setEmpId] = useState('');
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCatalog = () => api.competencyCatalog().then(setCatalog).catch(() => setCatalog([]));
  useEffect(() => {
    loadCatalog();
    api.employees().then((r) => setPeople(Array.isArray(r) ? r : r.rows || [])).catch(() => setPeople([]));
  }, []);
  useEffect(() => {
    if (!empId) { setProfile(null); return; }
    api.employeeCompetencies(empId).then(setProfile).catch((e) => showToast?.(e.message));
  }, [empId]); // eslint-disable-line

  const saveCompetency = async () => {
    if (!form.name || busy) return; setBusy(true);
    try { if (form.id) await api.updateCompetency(form.id, form); else await api.addCompetency(form); showToast?.('Saved'); setForm(null); loadCatalog(); if (empId) api.employeeCompetencies(empId).then(setProfile); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const remove = async (c) => { try { await api.deleteCompetency(c.id); loadCatalog(); } catch (e) { showToast?.(e.message); } };

  const setItem = (cid, key, val) => setProfile((p) => ({ ...p, items: p.items.map((it) => (it.competencyId === cid ? { ...it, [key]: Number(val) } : it)) }));
  const saveAssessment = async () => {
    if (busy) return; setBusy(true);
    try {
      const assessments = profile.items.map((it) => ({ competencyId: it.competencyId, level: it.level, target: it.target }));
      const r = await api.assessCompetencies(empId, assessments);
      setProfile(r); showToast?.('Assessment saved');
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };

  if (!catalog) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Competencies" sub="Define the framework and assess employees" />
        <PrimaryBtn onClick={() => setForm({ name: '', category: 'General', description: '', companyWide: false })}><Plus size={14} style={{ marginRight: 6 }} /> New competency</PrimaryBtn>
      </div>

      {/* Catalog */}
      <Card style={{ padding: 18 }}>
        <div className="flex items-center gap-2" style={{ marginBottom: 12 }}><Target size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Framework</span></div>
        {catalog.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No competencies defined yet.</p> : (
          <div style={{ display: 'grid', gap: 8 }}>
            {catalog.map((c) => (
              <div key={c.id} className="flex items-center justify-between" style={{ border: `1px solid ${AG.n200}`, borderRadius: 10, padding: '10px 12px' }}>
                <div><span style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{c.name}</span> <span style={{ fontSize: 12, color: AG.n500 }}>· {c.category}{c.entityId ? '' : ' · company-wide'}</span></div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setForm({ id: c.id, name: c.name, category: c.category, description: c.description || '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4, fontSize: 12, fontWeight: 600 }}>Edit</button>
                  <button onClick={() => remove(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Assess */}
      <Card style={{ padding: 18 }}>
        <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginBottom: 12 }}>
          <span style={{ fontWeight: 600, color: AG.text }}>Assess an employee</span>
          <select value={empId} onChange={(e) => setEmpId(e.target.value)} className="ag-input" style={{ width: 240 }}>
            <option value="">Select employee…</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.dept ? ` · ${p.dept}` : ''}</option>)}
          </select>
        </div>
        {!empId ? <p style={{ fontSize: 13, color: AG.n500 }}>Pick an employee to record their competency levels.</p>
          : !profile ? <CenterSpinner />
          : profile.items.length === 0 ? <EmptyState icon={Target} title="No competencies" desc="Define the framework first." />
          : (
            <>
              <div style={{ display: 'grid', gap: 8 }}>
                {profile.items.map((it) => (
                  <div key={it.competencyId} className="flex items-center justify-between" style={{ borderBottom: `1px solid ${AG.divider}`, paddingBottom: 8 }}>
                    <div><span style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{it.name}</span> <span style={{ fontSize: 12, color: AG.n500 }}>· {it.category}</span></div>
                    <div className="flex items-center gap-3">
                      <label style={{ fontSize: 12, color: AG.n600 }}>Level <select value={it.level} onChange={(e) => setItem(it.competencyId, 'level', e.target.value)} className="ag-input" style={{ width: 64, padding: '4px 6px', marginLeft: 4 }}>{[0, 1, 2, 3, 4, 5].map((n) => <option key={n}>{n}</option>)}</select></label>
                      <label style={{ fontSize: 12, color: AG.n600 }}>Target <select value={it.target} onChange={(e) => setItem(it.competencyId, 'target', e.target.value)} className="ag-input" style={{ width: 64, padding: '4px 6px', marginLeft: 4 }}>{[1, 2, 3, 4, 5].map((n) => <option key={n}>{n}</option>)}</select></label>
                      {it.gap > 0 ? <Pill tone="warning">gap {it.gap}</Pill> : <Pill tone="success">ok</Pill>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between" style={{ marginTop: 14 }}>
                <span style={{ fontSize: 12, color: AG.n500 }}>{profile.belowTarget} below target · avg gap {profile.avgGap}</span>
                <PrimaryBtn onClick={saveAssessment} disabled={busy}>Save assessment</PrimaryBtn>
              </div>
            </>
          )}
      </Card>

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit competency' : 'New competency'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <TextField fullWidth size="small" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Description (optional)" multiline minRows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              {!form.id && <FormControlLabel control={<Checkbox checked={!!form.companyWide} onChange={(e) => setForm({ ...form, companyWide: e.target.checked })} />} label="Available company-wide" />}
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={saveCompetency} disabled={busy}>Save</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
